import { errorMessage, warning } from '../../../../utils/errors';
import { parseInteriorForVictor } from '../../../../utils/parse-interior-for-victor';
import { transformPrNumber } from '../../../../utils/string-conversion';

import { HealEndpointFeature } from '../../../../services/victor/config-step-response';
import { rootStore } from '../../../root-store';
import { featureAppStoreActions } from '../../feature-app-store';
import { interiorStore } from '../../interior-store';
import { fetchConfigStart } from './fetch-config-start';
import { configStep } from './fetch-config-step';

export const COLOR_PREFIX = 'F14';
export const INTERIOR_PREFIX = 'F56';
export const RIM_PREFIX = 'MRAD';

export async function getBuildableConfiguration(cacheKey?: string) {
	const [_, currentModelValue] = rootStore.currentModel.get();
	if (!currentModelValue.vehicleConfiguration) {
		errorMessage('Unable to get current model, please contact dev team');
		return;
	}

	const [currentInteriorKey] = interiorStore.currentInterior.get();

	const { carlineCode, salesgroupCode, trimName } = currentModelValue.vehicleConfiguration;

	const [currentVariantKey] = rootStore.currentVariant.get();
	const [colorCode, rimCode] = currentVariantKey.split('-');

	const vehicleConfigurationData = await fetchConfigStart({
		cacheKey
	});

	const colorFeature: HealEndpointFeature = {
		code: transformPrNumber(`${COLOR_PREFIX}-${colorCode}`, 'external')
	};

	const rimCodeWithPrefix = `${RIM_PREFIX}${rimCode}`;
	const parentFeature = vehicleConfigurationData?.vehicleCatalogue?.features?.find(feature =>
		feature.packageFeatures?.includes(rimCodeWithPrefix)
	);

	const rimsFeature: HealEndpointFeature = {
		code: parentFeature?.code || `${RIM_PREFIX}${rimCode}`
	};

	const vehicleConfiguration = {
		vehicleConfiguration: {
			carlineKey: carlineCode || '',
			salesgroupKey: salesgroupCode || '',
			modelKey: vehicleConfigurationData?.vehicleConfiguration?.modelKey || '',
			modelYear: vehicleConfigurationData?.vehicleConfiguration?.modelYear || '',
			modelVersion: vehicleConfigurationData?.vehicleConfiguration?.modelVersion || '',
			exterior: colorFeature,
			features: [rimsFeature],
			interior: {
				code: currentInteriorKey
					? parseInteriorForVictor(`${INTERIOR_PREFIX}-${currentInteriorKey}`)
					: vehicleConfigurationData?.vehicleConfiguration?.interior?.code
			}
		}
	};

	try {
		const configStepResponse = await configStep({
			carConfiguration: vehicleConfiguration,
			cacheKey
		});

		if (!configStepResponse) return errorMessage('Failed to find buildable configuration. Please contact dev team');

		const {
			solvedVehicleConfiguration,
			validationData,
			vehicleConfiguration: fullVehicleConfiguration
		} = configStepResponse;

		const [_, currentModelValue] = rootStore.currentModel.get();
		if (!currentModelValue) {
			errorMessage('Unable to get currentModel from store, please contact dev team');
			return;
		}

		const victorServiceConfig = featureAppStoreActions.getServiceConfig('oneapi-victor');
		const category =
			victorServiceConfig?.tenantPrivate === currentModelValue.vehicleConfiguration?.tenant ? 'private' : 'commercial';

		const { conflicted, solutionCandidates } = validationData;

		if (!conflicted) {
			return {
				carlineId: fullVehicleConfiguration?.carlineKey,
				exteriorId: fullVehicleConfiguration?.exterior?.code || colorFeature.code,
				interiorId: fullVehicleConfiguration?.interior?.code,
				salesGroupId: fullVehicleConfiguration?.salesgroupKey,
				modelYear: fullVehicleConfiguration?.modelYear,
				modelId: fullVehicleConfiguration?.modelKey,
				modelVersion: fullVehicleConfiguration?.modelVersion,
				// we don't have a trimName in the buildable configuration, which is needed for the configure transfer
				trimName,
				category,
				options: [rimsFeature.code],
				buildabilityStatus: 'unknown'
			};
		}

		const solutionFeatureCodes = solutionCandidates!.reduce((acc, candidate) => {
			const featureCodes = candidate.addedFeatures.map(({ code }) => code);

			acc.push(...featureCodes);

			return acc;
		}, [] as string[]);

		// For debug purposes
		if (conflicted) {
			warning(`There is a buildability issue, to fix it following feature will be used ${solutionFeatureCodes.join()}`);
		}

		return {
			carlineId: solvedVehicleConfiguration?.carlineKey,
			exteriorId: solvedVehicleConfiguration?.exterior?.code || colorFeature.code,
			interiorId: solvedVehicleConfiguration?.interior?.code,
			salesGroupId: solvedVehicleConfiguration?.salesgroupKey,
			modelYear: solvedVehicleConfiguration?.modelYear,
			modelId: solvedVehicleConfiguration?.modelKey,
			modelVersion: solvedVehicleConfiguration?.modelVersion,
			// we don't have a trimName in the buildable configuration, which is needed for the configure transfer
			trimName,
			category,
			options: [...solutionFeatureCodes, rimsFeature.code],
			buildabilityStatus: 'unknown'
		};
	} catch (e) {
		errorMessage(`Coudln't get a full vehicle configuration. Verify the data driven setup\n ${e}`);
		throw e;
	}
}
